import { Flex } from '@ui-library/misc';
import { SelectV1, SelectV1Props } from '@ui-library/select';
import { Select } from 'antd';
import Tooltip from '@ui-library/tooltip';
import React from 'react';
import { InfoCircleTwoTone } from '@ant-design/icons';
import theme from '@css/theme';
import { FieldType } from '../types';
import classes from '../style.module.scss';

const { Option } = Select;
interface IFieldSelectOption
  extends Omit<Partial<FieldType>, 'value' | 'label'>,
    Pick<FieldType, 'value' | 'label'> {}
interface FieldSelectProps extends Omit<SelectV1Props, 'options'> {
  options: IFieldSelectOption[];
}

const FieldSelectOptionLabel: React.FC<IFieldSelectOption> = ({
  label,
  value,
  description,
  disabled,
  incompatibleFields,
}) => {
  let text = (
    <div
      data-testid={`description-disabled-${value}`}
      className={disabled ? classes.fieldOptionLabelDisabled : undefined}
    >
      {label}
    </div>
  );
  if (disabled && incompatibleFields) {
    text = (
      <Tooltip title={`This field is incompatible with ${incompatibleFields?.join(', ')}`}>
        {text}
      </Tooltip>
    );
  }
  return (
    <Flex alignItemsCenter>
      {text}
      {description && (
        <Tooltip title={description}>
          <InfoCircleTwoTone
            data-testid={`description-icon-${value}`}
            className={classes.fieldOptionLabelIcon}
            twoToneColor={theme.color.rsPrimary}
          />
        </Tooltip>
      )}
    </Flex>
  );
};

const FieldSelect: React.FC<FieldSelectProps> = ({ options, placeholder, ...props }) => (
  <SelectV1
    mode="multiple"
    maxTagCount={0}
    optionLabelProp="label"
    placeholder={placeholder}
    maxTagPlaceholder={(values) => `${placeholder || 'Selected values'} (${values.length})`}
    {...props}
  >
    {options.map(({ label, description, disabled, incompatibleFields, value, ...optionsProps }) => (
      <Option
        value={value}
        data-testid={`option-${value}`}
        key={`option-${value}`}
        {...optionsProps}
        disabled={disabled}
        label={label}
      >
        <FieldSelectOptionLabel
          label={label}
          description={description}
          disabled={disabled}
          incompatibleFields={incompatibleFields}
          value={value}
          {...optionsProps}
        />
      </Option>
    ))}
  </SelectV1>
);

export default FieldSelect;
